import { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { loadTenantBrandingAsync } from "./tenantBrandingSlice";

import { setDateDisplayConfig } from "@/lib/date";
import type { AppDispatch, RootState } from "@/redux/store";
import { resetCurrencyDisplayConfig, setCurrencyDisplayConfig } from "@/utils/currency";

export function TenantBrandingBootstrap() {
  const dispatch = useDispatch<AppDispatch>();
  const {
    initialized,
    isAuthenticated,
    tenantId,
    currentTenantId,
    currentTimeZoneId,
    currentCurrencyCode,
    currentCountryCode,
    currentLocale,
    loading,
    currentExpiresAt,
    failedTenantId,
  } = useSelector(
    (state: RootState) => ({
      initialized: state.auth.initialized,
      isAuthenticated: state.auth.isAuthenticated,
      tenantId: state.auth.user?.tenantId ?? null,
      currentTenantId: state.tenantBranding.currentTenantId,
      currentTimeZoneId: state.tenantBranding.current?.timeZoneId ?? null,
      currentCurrencyCode: state.tenantBranding.current?.currencyCode ?? null,
      currentCountryCode: state.tenantBranding.current?.countryCode ?? null,
      currentLocale: state.tenantBranding.current?.locale ?? null,
      loading: state.tenantBranding.loading,
      currentExpiresAt: state.auth.user?.tenantId
        ? (state.tenantBranding.cache[state.auth.user.tenantId]?.expiresAt ?? null)
        : null,
      failedTenantId: state.tenantBranding.failedTenantId,
    }),
    shallowEqual,
  );

  // The single line that turns lib/date.ts's timezone config from dead code into the display
  // layer: every formatDate/formatDateTime call renders in the tenant's own zone from here on.
  // Runs whenever the loaded tenant (or its zone) changes, and falls back to null — browser-local,
  // the pre-existing default — once branding is cleared (logout, or before it has loaded).
  useEffect(() => {
    setDateDisplayConfig({
      timezone: currentTenantId === tenantId ? currentTimeZoneId : null,
    });
  }, [currentTenantId, currentTimeZoneId, tenantId]);

  // The money counterpart of the line above: every formatMoney call renders in the tenant's own
  // currency and number grouping from here on. Both are reset together when branding is cleared, so
  // amounts and dates can never disagree about which tenant is on screen.
  //
  // Locale matters as much as the symbol: en-IN groups as 1,00,000 while en-AE groups as 100,000,
  // and Intl handles that difference for every market from the one tag.
  useEffect(() => {
    if (currentTenantId === tenantId && currentCurrencyCode) {
      setCurrencyDisplayConfig({
        currencyCode: currentCurrencyCode,
        locale: currentLocale ?? undefined,
        countryCode: currentCountryCode ?? undefined,
      });
    } else {
      resetCurrencyDisplayConfig();
    }
  }, [currentTenantId, currentCurrencyCode, currentCountryCode, currentLocale, tenantId]);

  useEffect(() => {
    if (!initialized || !isAuthenticated || !tenantId) {
      return;
    }

    if (loading && currentTenantId === tenantId) {
      return;
    }

    if (
      currentTenantId === tenantId &&
      currentExpiresAt &&
      Date.parse(currentExpiresAt) > Date.now()
    ) {
      return;
    }

    // The last attempt for this tenant failed — don't auto-retry every render (that
    // starves the tab in an unbounded request loop). A fresh attempt only happens for
    // a different tenant, or after something explicitly clears the failure (reload,
    // re-login, a user-triggered retry).
    if (failedTenantId === tenantId) {
      return;
    }

    dispatch(loadTenantBrandingAsync({ tenantId }));
  }, [
    currentExpiresAt,
    currentTenantId,
    dispatch,
    failedTenantId,
    initialized,
    isAuthenticated,
    loading,
    tenantId,
  ]);

  return null;
}
